
import { Component, OnInit } from '@angular/core';
import { AppState, ShowRequest } from '../../core';
import { CallLogRecord } from '../../core/models';
import { DefaultCellRendererComponent, numberToFilterString, TableViewQueryCriteria } from '../../shared/components/table-view/table-view-component-base';
import { BsModalService } from 'ngx-bootstrap/modal';
import { VolunteerService } from '../../volunteer/services/volunteer.service';
import { VolunteerEditComponent } from '../../volunteer/volunteer-edit/volunteer-edit.component';
import { VolunteerHandsetRec } from '../../volunteer/models/volunteer-handset';
import { firstValueFrom } from 'rxjs';
import { PathTestService, PostProcessingService, UserService } from '../../core/services';
import { UserEditComponent } from '../../user/user-edit/user-edit.component';
import { TestSeqService } from '../../core/services/test-seq.service';
import { TestDocArgs } from '../../path-test/path-test.component';


@Component({
    selector: '[fes-call-logs-table-cell-renderer]',
    templateUrl: 'call-logs-table-cell.component.html',
    styleUrls: ['call-logs-table-cell.component.scss'],
    host: { '[class]': "cellClass" }
  })
  export class CallLogsTableCellComponent extends DefaultCellRendererComponent<CallLogRecord> implements OnInit {

    cellClass: string

    constructor(
      public appState: AppState,
      public modalService: BsModalService,
      public testbedUserService: UserService,
      public volunteerService: VolunteerService,
      public testSeqService: TestSeqService,
      public pathTestService: PathTestService,
      public postProcessingService: PostProcessingService,
    ) {
      super();
    }

    ngOnInit(): void {
      if ( this.column.template == 'correlationLink' ) {
        const startTimeCss = this.record[this.column?.name] ? 'start-time-found':'start-time-missing'
        this.cellClass = this.column.dataCellCssClass ?
            this.column.dataCellCssClass  + ' ' + startTimeCss : startTimeCss
      }
    }


    showCorrelation(rec:CallLogRecord){
      console.log(`showCorrelation(${rec.phonenum})`)
      const firstPageCriteria : TableViewQueryCriteria = {
        page: {
          firstRowFilters: [
            { key: 'locsourceid', mode: '=', value: numberToFilterString(rec.locsourceid)},
            { key: 'callsourceid', mode: '=', value: numberToFilterString(rec.sourceid)}
          ]
        }
      }
      this.tableComponent.applySelectedRecords({recs: [rec]});
      const strPageSearch = JSON.stringify(firstPageCriteria)
      this.showRequestEmitter.emit( new ShowRequest('tab', 'correlation', strPageSearch, null))
    }

    editUser = async (rec: CallLogRecord) => {
      try {
        this.tableComponent.applySelectedRecords({recs: [rec]});
        if ( rec.testtype == 'rut') {
          const volunteer = await this.volunteerService.getByIdEagerly(rec.userid);
          if ( !volunteer ) {
            this.appState.notifyWarning('Volunteer not found',this,3);
            return;
          }
          const modal = VolunteerEditComponent.showModal(this.modalService,volunteer);
          let updatedHandset: VolunteerHandsetRec = null;
          const handsetSubscription = modal.content.handsetChanged.subscribe({
            next: (handsetRec) => updatedHandset = handsetRec
          })
          await firstValueFrom(modal.onHidden);
          handsetSubscription.unsubscribe();
          if ( !modal.content?.cancelled || updatedHandset) {
            this.tableComponent.load().catch(e=>this.volunteerService.publishError);
          }
        } else if ( rec.testtype == 'dialer') {
          const user = await this.testbedUserService.getById(rec.userid);
          if ( !user ) {
            this.appState.notifyWarning('User not found',this,3);
            return;
          }
          const initialState = {
            user
          }
          this.modalService.show(UserEditComponent, {ignoreBackdropClick: true, initialState});
        }
      } catch (e:any) {
        console.error(e);
        this.appState.notifyWarning('Could not get user details. ' + (e.message || e),this,3);
      }
    }

    showTask = async (rec: CallLogRecord) => {
      try {
        this.tableComponent.applySelectedRecords({recs: [rec]});
        if ( rec.testtype == 'rut') {
          this.showRequestEmitter.emit( new ShowRequest('tab', "volunteer task", rec.taskid, null))
        } else if ( rec.testtype == 'dialer') {
          try {
          this.tableComponent.isLoading = true;
          const pathTestDoc = await this.pathTestService.findOneByTestPointId(rec.testpointid);
          if ( !pathTestDoc ) {
            this.appState.notifyWarning('Path test not found',this,3);
            return;
          }
          this.showRequestEmitter.emit( new ShowRequest('tab', "path test", null, pathTestDoc))
          } finally {
            this.tableComponent.isLoading = false;
          }
        }
      } catch (e:any) {
        console.error(e);
        this.appState.notifyWarning('Could not get user details. ' + (e.message || e),this,3);
      }
    }

    showTestPoint = async (rec: CallLogRecord) => {
      try {
        this.tableComponent.applySelectedRecords({recs: [rec]});
        if ( rec.testtype == 'rut') {
          const taskIdObj = {
            taskId: rec.taskid,
            taskPointId: parseInt(rec.testpointid)
          }
          this.showRequestEmitter.emit( new ShowRequest('tab', "volunteer task", JSON.stringify(taskIdObj), null))
        } else if ( rec.testtype == 'dialer') {
          const testDocArgs: TestDocArgs = {
            pointTestId: rec.testpointid,
            editPoint: true,
          }
          // Note: drive test is not supported from call_view
          this.showRequestEmitter.emit( new ShowRequest('tab', 'path test', JSON.stringify(testDocArgs), null))
        }
      } catch (e:any) {
        console.error(e);
        this.appState.notifyWarning('Could not get user details. ' + (e.message || e),this,3);
      }
    }


    showCall(rec:CallLogRecord){
      const queryCriteria : TableViewQueryCriteria = {
        page: {
          firstRowFilters: [
            // TODO - this needs to make sense for legacy, FES dialer, and volunteer (RUT) calls
            //{ key: 'locsourceid', mode: '=', value: numberToFilterString(record.locsourceid)},
            { key: 'id', mode: '=', value: numberToFilterString(rec.sourceid)},
            //{ key: 'starttime', mode: '=', value: starttime}
          ]
        }
      }
      let tabType = ''
      if ( rec.testtype == 'dialer') {
        tabType = 'dialer call logs';
      } else if ( rec.testtype == 'rut') {
        tabType = 'rut call logs'
      } else {
        this.appState.notifyWarning(`No table view for test type ${rec.testtype}`,this,3);
        return;
      }
      this.tableComponent.applySelectedRecords({recs: [rec]});
      this.showRequestEmitter.emit( new ShowRequest('tab', tabType, JSON.stringify(queryCriteria), null))
    }

  }